import React, { useLayoutEffect, useRef } from "react";
import styled from "styled-components";
import { CheckOutlined, LeftOutlined, PlusOutlined, RightOutlined } from "@ant-design/icons";
import { TAP_TARGET, TAP_TARGET_PRIMARY, mediaPhone } from "@/shared/theme/breakpoints";
import { pressedDarken, pressedTint } from "@/shared/theme/pressed";
import { CUSTOM_COLORS } from "@/shared/theme/colorUtils";
import { ActionBar } from "@/shared/components";

/*
 * Phone presentation of the vetting chain: one level at a time instead of side-by-side columns.
 * Purely presentational — VettingChain owns the path, the levels and every rule about what a
 * block can do, and passes the current level in as rows.
 */

export interface VettingNavigatorMarket {
	id: string;
	code: string;
	name: string;
	flagUrl: string;
}

export interface VettingNavigatorCrumb {
	id: string;
	name: string;
	icon: React.ReactNode;
}

export interface VettingNavigatorRowAction {
	key: string;
	label: string;
	danger?: boolean;
	disabled?: boolean;
	onSelect: () => void;
}

export interface VettingNavigatorRow {
	id: string;
	name: string;
	icon: React.ReactNode;
	/** The block whose content is open (pink fill, as on desktop). */
	active: boolean;
	/** An ancestor of the open block (pink outline). */
	inPath: boolean;
	/** Tapping the row opens this block's content. */
	selectable: boolean;
	/** The block has a level below it; shown as a separate chevron target when also selectable. */
	drillable: boolean;
	statusText?: string;
	completed?: boolean;
	/** Edit-mode menu (mark complete, delete), behind a "More" button on the row. */
	actions?: VettingNavigatorRowAction[];
}

interface VettingNavigatorProps {
	className?: string;
	markets: VettingNavigatorMarket[];
	activeMarketId: string | null;
	onMarketSelect: (marketId: string) => void;
	/** Levels between the market and the current one; the last is the current level. */
	crumbs: VettingNavigatorCrumb[];
	onCrumbSelect: (id: string) => void;
	onUp?: () => void;
	rows: VettingNavigatorRow[];
	onRowSelect: (id: string) => void;
	onRowDrill: (id: string) => void;
	onAdd?: () => void;
	completion?: { label: string; onToggle: () => void } | null;
}

const Root = styled.div`
	display: none;

	${mediaPhone`
		display: flex;
		flex-direction: column;
		background: #eff1f9;
	`}
`;

/* ─── Market rail ─── */

const MarketRail = styled.div`
	display: flex;
	gap: 4px;
	padding: 0 8px;
	background: #fafbff;
	border-bottom: 1px solid #dde2fa;
	overflow-x: auto;
	scrollbar-width: none;

	&::-webkit-scrollbar {
		display: none;
	}
`;

const MarketTab = styled.button<{ $active: boolean }>`
	position: relative;
	display: inline-flex;
	align-items: center;
	gap: 8px;
	flex-shrink: 0;
	height: ${TAP_TARGET_PRIMARY}px;
	padding: 0 12px;
	border: none;
	background: transparent;
	color: ${({ $active }) => ($active ? "#f72586" : "#555")};
	font-family: inherit;
	font-size: 14px;
	font-weight: 600;
	cursor: pointer;

	&::after {
		content: "";
		position: absolute;
		left: 8px;
		right: 8px;
		bottom: 0;
		height: 3px;
		border-radius: 3px 3px 0 0;
		background: ${({ $active }) => ($active ? "#f72586" : "transparent")};
	}

	&:focus-visible {
		outline: 2px solid #3a0ca3;
		outline-offset: -2px;
	}

	${pressedTint}
`;

const Flag = styled.img`
	width: 20px;
	height: 20px;
	border-radius: 50%;
	object-fit: cover;
	flex-shrink: 0;
`;

/* ─── Breadcrumb ─── */

const CrumbBar = styled.nav`
	display: flex;
	align-items: center;
	min-height: ${TAP_TARGET}px;
	padding-right: 12px;
	background: #fff;
	border-bottom: 1px solid #dde2fa;
`;

const UpButton = styled.button`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
	width: ${TAP_TARGET}px;
	height: ${TAP_TARGET}px;
	border: none;
	background: transparent;
	color: #3a0ca3;
	cursor: pointer;

	&:focus-visible {
		outline: 2px solid #3a0ca3;
		outline-offset: -2px;
	}

	${pressedTint}
`;

/*
 * The whole trail, at full names. When it's wider than the screen it scrolls sideways, starting
 * at the current level, and fades at the left edge while earlier levels are out of view.
 */
const CrumbList = styled.ol`
	display: flex;
	flex: 1 1 auto;
	align-items: center;
	gap: 2px;
	min-width: 0;
	margin: 0;
	padding: 0;
	list-style: none;
	overflow-x: auto;
	overscroll-behavior-x: contain;
	scrollbar-width: none;

	&::-webkit-scrollbar {
		display: none;
	}

	&[data-clipped="true"] {
		mask-image: linear-gradient(to right, transparent, #000 24px);
		-webkit-mask-image: linear-gradient(to right, transparent, #000 24px);
	}
`;

const Crumb = styled.li`
	display: inline-flex;
	flex: none;
	align-items: center;
	gap: 2px;
	font-size: 13px;
	color: #c4c4c4;
`;

const CrumbIcon = styled.span`
	display: inline-flex;
	flex: none;
`;

const CrumbButton = styled.button`
	display: inline-flex;
	align-items: center;
	gap: 6px;
	min-height: ${TAP_TARGET}px;
	padding: 0 4px;
	border: none;
	background: transparent;
	color: #666;
	font-family: inherit;
	font-size: 13px;
	font-weight: 500;
	min-width: 0;
	white-space: nowrap;
	cursor: pointer;

	/* Ancestors' icons sit back, as in the desktop breadcrumb. */
	&:not([aria-current]) ${CrumbIcon} {
		opacity: 0.5;
	}

	&[aria-current="location"] {
		color: #f72586;
		font-weight: 600;
		cursor: default;
	}

	${pressedTint}
`;

/* ─── Level list ─── */

const ROW_HEIGHT = 56;
const ROW_GAP = 8;
const LIST_PADDING = 12;
const VISIBLE_ROWS = 4;

/*
 * Always exactly four rows tall, whether the level has one block or forty, so the panel never
 * jumps as you drill. Longer levels scroll inside, with a visible brand-tinted thumb.
 */
const List = styled.ul`
	display: flex;
	flex-direction: column;
	gap: ${ROW_GAP}px;
	height: ${VISIBLE_ROWS * ROW_HEIGHT + (VISIBLE_ROWS - 1) * ROW_GAP + 2 * LIST_PADDING}px;
	margin: 0;
	padding: ${LIST_PADDING}px;
	position: relative;
	list-style: none;
	overflow-y: auto;
	overscroll-behavior: contain;
	scrollbar-width: thin;
	scrollbar-color: #bdc6f2 transparent;

	&::-webkit-scrollbar {
		width: 4px;
	}

	&::-webkit-scrollbar-thumb {
		border-radius: 2px;
		background: #bdc6f2;
	}

	&::-webkit-scrollbar-track {
		background: transparent;
	}

	> li {
		flex-shrink: 0;
	}
`;

const Row = styled.li<{ $active: boolean; $inPath: boolean }>`
	display: flex;
	align-items: stretch;
	min-height: ${ROW_HEIGHT}px;
	border-radius: 12px;
	overflow: hidden;
	background: ${({ $active }) => ($active ? "#f72586" : "#fff")};
	color: ${({ $active }) => ($active ? "#fff" : "#333")};
	border: 1px solid ${({ $active, $inPath }) => ($active || $inPath ? "#f72586" : "#e8e8e8")};
	box-shadow: ${({ $active, $inPath }) =>
		$active || $inPath ? "0px 4px 12px 0px #F725863D" : "0 1px 3px rgba(0,0,0,0.06)"};
`;

const RowMain = styled.button<{ $active: boolean }>`
	display: flex;
	align-items: center;
	gap: 10px;
	flex: 1;
	min-width: 0;
	padding: 8px 14px;
	border: none;
	background: transparent;
	color: inherit;
	font-family: inherit;
	font-size: 14px;
	text-align: left;
	cursor: pointer;

	&:disabled {
		cursor: default;
	}

	&:focus-visible {
		outline: 2px solid ${({ $active }) => ($active ? "#fff" : "#3a0ca3")};
		outline-offset: -2px;
	}

	${({ $active }) => ($active ? pressedDarken : pressedTint)}
`;

const RowIcon = styled.span<{ $light: boolean }>`
	display: flex;
	align-items: center;
	flex-shrink: 0;

	img {
		filter: ${({ $light }) => ($light ? "brightness(0) invert(1)" : "none")};
	}
`;

const RowText = styled.span`
	display: flex;
	flex-direction: column;
	flex: 1;
	min-width: 0;
`;

const RowName = styled.span<{ $emphasized: boolean }>`
	overflow: hidden;
	font-weight: ${({ $emphasized }) => ($emphasized ? 600 : 500)};
	text-overflow: ellipsis;
	white-space: nowrap;
`;

const RowStatus = styled.span<{ $light: boolean }>`
	overflow: hidden;
	color: ${({ $light }) => ($light ? "#fff" : CUSTOM_COLORS.PRIMARY)};
	font-size: 11px;
	line-height: 16px;
	text-overflow: ellipsis;
	white-space: nowrap;
`;

const CompletedMark = styled.span`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
	width: 18px;
	height: 18px;
	border-radius: 50%;
	background: #bdf28d;
	color: #4d9e20;
	font-size: 10px;
`;

const DrillButton = styled.button<{ $active: boolean }>`
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
	width: ${TAP_TARGET + 4}px;
	border: none;
	border-left: 1px solid ${({ $active }) => ($active ? "rgba(255, 255, 255, 0.4)" : "#eceef7")};
	background: transparent;
	color: ${({ $active }) => ($active ? "#fff" : "#9e9e9e")};
	font-size: 12px;
	cursor: pointer;

	&:focus-visible {
		outline: 2px solid ${({ $active }) => ($active ? "#fff" : "#3a0ca3")};
		outline-offset: -2px;
	}

	${({ $active }) => ($active ? pressedDarken : pressedTint)}
`;

const RowMenu = styled(ActionBar)<{ $active: boolean }>`
	align-self: center;
	margin-right: 6px;

	button[aria-haspopup] {
		border: none;
		background: transparent;
		color: ${({ $active }) => ($active ? "#fff" : "#707070")};
	}
`;

const TrailingChevron = styled(RightOutlined)`
	flex-shrink: 0;
	font-size: 12px;
	opacity: 0.6;
`;

const Empty = styled.p`
	margin: 0;
	padding: 24px 16px;
	color: #767676;
	font-size: 14px;
	text-align: center;
`;

const AddRow = styled.button`
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 6px;
	min-height: ${TAP_TARGET_PRIMARY}px;
	border: 1px dashed #bdc6f2;
	border-radius: 12px;
	background: transparent;
	color: #3a0ca3;
	font-family: inherit;
	font-size: 14px;
	font-weight: 500;
	cursor: pointer;

	${pressedTint}
`;

const CompletionBar = styled.div`
	padding: 12px;
	border-top: 1px solid #dde2fa;
	background: #fff;
`;

const CompletionButton = styled.button`
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 8px;
	width: 100%;
	min-height: ${TAP_TARGET_PRIMARY}px;
	border: 1px solid #3a0ca3;
	border-radius: 999px;
	background: #fff;
	color: #3a0ca3;
	font-family: inherit;
	font-size: 15px;
	font-weight: 600;
	cursor: pointer;

	${pressedTint}
`;

export const VettingNavigator: React.FC<VettingNavigatorProps> = ({
	className,
	markets,
	activeMarketId,
	onMarketSelect,
	crumbs,
	onCrumbSelect,
	onUp,
	rows,
	onRowSelect,
	onRowDrill,
	onAdd,
	completion,
}) => {
	const currentLevel = crumbs[crumbs.length - 1];
	const crumbListRef = useRef<HTMLOListElement>(null);

	const markClippedCrumbs = () => {
		const trail = crumbListRef.current;
		if (trail) trail.dataset.clipped = String(trail.scrollLeft > 0);
	};

	// Open each level with the trail scrolled to its end, so the current level is in view.
	useLayoutEffect(() => {
		const trail = crumbListRef.current;
		if (!trail) return;
		trail.scrollLeft = trail.scrollWidth;
		markClippedCrumbs();
	}, [currentLevel?.id]);
	const listRef = useRef<HTMLUListElement>(null);
	const activeRowId = rows.find((row) => row.active || row.inPath)?.id;

	// Keep the selected block inside the four-row window, without scrolling the page.
	useLayoutEffect(() => {
		const list = listRef.current;
		const row = activeRowId ? list?.querySelector<HTMLElement>(`[data-row-id="${CSS.escape(activeRowId)}"]`) : null;
		if (!list || !row) return;
		const top = row.offsetTop - LIST_PADDING;
		const bottom = row.offsetTop + row.offsetHeight + LIST_PADDING - list.clientHeight;
		if (list.scrollTop > top) list.scrollTop = top;
		else if (list.scrollTop < bottom) list.scrollTop = bottom;
	}, [activeRowId, currentLevel?.id]);

	return (
		<Root className={className}>
			{markets.length > 0 && (
				<MarketRail role="tablist" aria-label="Markets">
					{markets.map((market) => (
						<MarketTab
							key={market.id}
							type="button"
							role="tab"
							aria-selected={market.id === activeMarketId}
							title={market.name}
							$active={market.id === activeMarketId}
							onClick={() => onMarketSelect(market.id)}
						>
							{market.flagUrl && <Flag src={market.flagUrl} alt="" />}
							{market.code}
						</MarketTab>
					))}
				</MarketRail>
			)}

			{crumbs.length > 0 && (
				<CrumbBar aria-label="Current level">
					{onUp && (
						<UpButton type="button" aria-label="Up one level" onClick={onUp}>
							<LeftOutlined />
						</UpButton>
					)}
					<CrumbList
						ref={crumbListRef}
						style={onUp ? undefined : { paddingLeft: 12 }}
						onScroll={markClippedCrumbs}
					>
						{crumbs.map((crumb, index) => {
							const isCurrent = index === crumbs.length - 1;
							return (
								<Crumb key={crumb.id}>
									{index > 0 && <span aria-hidden>›</span>}
									<CrumbButton
										type="button"
										aria-current={isCurrent ? "location" : undefined}
										onClick={isCurrent ? undefined : () => onCrumbSelect(crumb.id)}
									>
										<CrumbIcon>{crumb.icon}</CrumbIcon>
										{crumb.name}
									</CrumbButton>
								</Crumb>
							);
						})}
					</CrumbList>
				</CrumbBar>
			)}

			<List ref={listRef} aria-label={currentLevel ? currentLevel.name : "Blocks"}>
				{rows.length === 0 && !onAdd && (
					<li>
						<Empty>Nothing here yet</Empty>
					</li>
				)}
				{rows.map((row) => {
					// A row that only leads somewhere drills when tapped; one that also has content
					// opens it and keeps the chevron as a separate target.
					const splitTargets = row.selectable && row.drillable;
					const onMain = row.selectable
						? () => onRowSelect(row.id)
						: row.drillable
							? () => onRowDrill(row.id)
							: undefined;
					return (
						<Row key={row.id} data-row-id={row.id} $active={row.active} $inPath={row.inPath}>
							<RowMain
								type="button"
								$active={row.active}
								disabled={!onMain}
								aria-current={row.active ? "page" : undefined}
								onClick={onMain}
							>
								{row.icon && <RowIcon $light={row.active}>{row.icon}</RowIcon>}
								<RowText>
									<RowName $emphasized={row.active || row.inPath}>{row.name}</RowName>
									{row.statusText && <RowStatus $light={row.active}>{row.statusText}</RowStatus>}
								</RowText>
								{row.completed && (
									<CompletedMark aria-label="Completed">
										<CheckOutlined />
									</CompletedMark>
								)}
								{!splitTargets && row.drillable && <TrailingChevron aria-hidden />}
							</RowMain>
							{splitTargets && (
								<DrillButton
									type="button"
									$active={row.active}
									aria-label={`Open ${row.name}`}
									onClick={() => onRowDrill(row.id)}
								>
									<RightOutlined />
								</DrillButton>
							)}
							{row.actions && row.actions.length > 0 && (
								<RowMenu
									$active={row.active}
									moreLabel={`${row.name} actions`}
									actions={row.actions.map((action) => ({
										...action,
										priority: "overflow" as const,
									}))}
								/>
							)}
						</Row>
					);
				})}
				{onAdd && (
					<li style={{ display: "flex", flexDirection: "column" }}>
						<AddRow type="button" onClick={onAdd}>
							<PlusOutlined style={{ fontSize: 12 }} />
							Add
						</AddRow>
					</li>
				)}
			</List>

			{completion && (
				<CompletionBar>
					<CompletionButton type="button" onClick={completion.onToggle}>
						<CheckOutlined />
						{completion.label}
					</CompletionButton>
				</CompletionBar>
			)}
		</Root>
	);
};
